/**
 * Générateur — Attendu 963 — Définir le parallélogramme.
 *
 * Niveau : 5ème | Thème #11 Parallélogrammes (O22) | Domaine : Espace et géométrie
 * Source prof : cluster 1, Ex.1 (croisement de bandes) + Ex.3 (LION de centre R),
 * extraction exerciseur/parallelogrammes.
 *
 * 3 formats / 2 cartes :
 *  1. bandes — Ex.1 : ADAPTATION question ouverte → QCM (définition en acte :
 *     « côtés opposés parallèles ») + relier (condition sur les bandes ↔ nature).
 *  2. lion   — Ex.3 : LION parallélogramme de centre R, menus déroulants — le
 *     pont avec la symétrie centrale (image d'un point / d'un segment, côtés
 *     opposés, angles opposés, demi-diagonales). Figure codée.
 *
 * INVARIANTS : la figure LION est un VRAI parallélogramme (A, A+u, A+u+v, A+v),
 * donc R = milieu des diagonales, côtés opposés égaux, angles opposés égaux.
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeRelier, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import {
    paraParVecteurs, dessineQuad, figEl, chapeau, salt,
} from './svgParallelo';

const ATTENDU_ID = 963;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_BANDES = 'Le croisement de deux bandes';
const L_LION = 'LION, parallélogramme de centre R';

const VB = { w: 320, h: 240 };

// ═══════════════════════════════════════════════════════════
// Carte 1 — Le croisement de deux bandes (Ex.1)
// ═══════════════════════════════════════════════════════════

const COULEURS = ['rouge', 'bleue', 'verte', 'jaune', 'orange', 'violette'];
const CHOIX_QUAD = ['Parallélogramme', 'Losange', 'Rectangle', 'Carré'];

/** Quadrilatère obtenu au croisement de deux bandes selon largeur / perpendicularité. */
const quadBande = (memeLargeur, perp) =>
    perp ? (memeLargeur ? 'Carré' : 'Rectangle') : (memeLargeur ? 'Losange' : 'Parallélogramme');

const genBandesQcm = () => {
    const [c1, c2] = shuffle(COULEURS).slice(0, 2);
    const memeLargeur = Math.random() < 0.5;
    const perp = Math.random() < 0.5;
    const w = randInt(2, 6);
    let w2 = randInt(2, 6);
    while (w2 === w) w2 = randInt(2, 6);
    const largeurTxt = memeLargeur
        ? `la même largeur (${w} cm)`
        : `des largeurs différentes (${w} cm et ${w2} cm)`;
    const perpTxt = perp ? 'perpendiculaires' : 'sécantes mais non perpendiculaires';
    const correct = quadBande(memeLargeur, perp);
    const texte = `<p>On croise une bande ${c1} et une bande ${c2} de papier. `
        + `Elles ont ${largeurTxt} et sont ${perpTxt}. `
        + `Quel quadrilatère obtient-on à leur intersection ?</p>`;
    const payload = makeQcm(CHOIX_QUAD, correct, texte, { ...opts(L_BANDES) });
    payload._meta.generatorKey = 'bandes_qcm_unique';
    return payload;
};

const genBandesRelier = () => {
    const w = () => randInt(2, 6);
    const diff = () => { const a = w(); let b = w(); while (b === a) b = w(); return [a, b]; };
    const [a1, b1] = diff();
    const [a2, b2] = diff();
    const pairs = shuffle([
        { source: `Largeurs différentes (${a1} cm et ${b1} cm), non perpendiculaires`, target: 'Parallélogramme' },
        { source: `Même largeur (${w()} cm), non perpendiculaires`, target: 'Losange' },
        { source: `Largeurs différentes (${a2} cm et ${b2} cm), perpendiculaires`, target: 'Rectangle' },
        { source: `Même largeur (${w()} cm), perpendiculaires`, target: 'Carré' },
    ]);
    const texte = '<p>On croise deux bandes de papier. Relie chaque situation au quadrilatère '
        + 'obtenu à leur intersection.</p>';
    const payload = makeRelier(pairs, texte, { ...opts(L_BANDES) });
    payload._meta.generatorKey = 'bandes_relier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — LION, parallélogramme de centre R (Ex.3)
// ═══════════════════════════════════════════════════════════

const figLion = (nL, nI, nO, nN, nR, s) => {
    // Ordre A-B-C-D = L-I-O-N autour de la figure ; centre = R.
    const L = { x: 0, y: 0 };
    const u = { x: randInt(110, 140), y: -randInt(4, 16) };   // L→I (≈ horizontal)
    const v = { x: randInt(28, 52), y: -randInt(70, 92) };    // L→N (montant)
    const { A, B, C, D } = paraParVecteurs(L, u, v);
    const { fig } = dessineQuad([A, B, C, D], VB, s, {
        noms: [nL, nI, nO, nN],
        diagonales: true,
        centre: { label: nR },
    });
    return fig;
};

const genLion = () => {
    // Quatre lettres distinctes pour les sommets + une pour le centre.
    const [nL, nI, nO, nN, nR] = shuffle('ABCDEGHJKMPRSTUVZ'.split('')).slice(0, 5);
    const fig = figLion(nL, nI, nO, nN, nR, salt(randInt(0, 9999)));

    // Sommets ordonnés L-I-O-N : la symétrie de centre R échange L↔O et I↔N.
    const angI = chapeau(nI);       // angle au sommet I
    const angN = chapeau(nN);       // opposé (égal)
    const phrase =
        `${nN} est l'image de {{${nI}}} par la symétrie de centre {{${nR}}}. `
        + `L'image du segment [${nI}${nL}] par cette symétrie est {{[${nN}${nO}]}}. `
        + `Le côté [${nI}${nO}] a la même longueur que {{[${nN}${nL}]}}. `
        + `L'angle ${angI} est égal à l'angle {{${angN}}}. `
        + `Enfin ${nR}${nL} = {{${nR}${nO}}} car les diagonales se coupent en leur milieu.`;
    // ⚠️ pas de segment RENVERSÉ d'une réponse : [ON] ≡ [NO] serait une 2e bonne
    // étiquette. Réponses-segments = [NO] et [NL] → on évite [ON] et [LN].
    const distract = filtrerDistracteurs(phrase, [
        nL, nO,
        `[${nI}${nO}]`, `[${nL}${nI}]`, `[${nL}${nO}]`,
        chapeau(nL), chapeau(nO),
        `${nR}${nI}`, `${nR}${nN}`,
    ]);
    const texte = `<p>${nL}${nI}${nO}${nN} est un parallélogramme de centre ${nR}. `
        + 'Complète en utilisant la symétrie de centre ' + nR + ' :</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_LION), mode: 'drag', distracteurs: distract,
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'lion_texte_trou_drag';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    bandes_qcm_unique: genBandesQcm,
    bandes_relier: genBandesRelier,
    lion_texte_trou_drag: genLion,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier', texte_trou_drag: 'Menus',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'bandes', label: L_BANDES, formatsList: buildFormats('bandes') },
    { id: 'lion', label: L_LION, formatsList: buildFormats('lion') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { genBandesQcm, genBandesRelier, genLion };
